/**
 * Shared modal scaffolding for the Size & Paper flow (crop editor, review
 * tray, first-time guide). Handles: dimmed backdrop, Escape-to-close (topmost
 * modal only, via a module-level stack so stacked modals close one at a time),
 * backdrop-click close, body scroll lock, and initial focus.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';

/** Stack of open modal ids — Escape only closes the top one. */
const modalStack: string[] = [];

export interface ModalShellProps {
  onClose: () => void;
  /** Max width of the dialog card — px number or any CSS width (e.g. `min(720px, 92vw)`). */
  width: number | string;
  /** Backdrop opacity (design: .92 for editors/tray, .88 for the guide). */
  dim?: number;
  /** Accessible name for the dialog. */
  label: string;
  /** Extra class on the dialog card. */
  className?: string;
  children: ReactNode;
}

export function ModalShell({ onClose, width, dim = 0.92, label, className, children }: ModalShellProps) {
  const id = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    modalStack.push(id);
    const prevFocus = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cardRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === id) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const idx = modalStack.indexOf(id);
      if (idx !== -1) modalStack.splice(idx, 1);
      if (modalStack.length === 0) document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [id]);

  return (
    <div
      className="mshell"
      style={{ background: `rgba(26, 26, 26, ${dim})` }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`mshell__card${className ? ` ${className}` : ''}`}
        style={{ width: '100%', maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
